import React, { FC, ReactNode, useMemo } from "react";
import { Modal, Button, Tooltip } from "antd";
import "./ConfirmationModal.scss";
import StatusIcon from "../statusIcon/StatusIcon";

type ConfirmationModalProps = {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onClose: () => void;
  title: string;
  description?: string;
  confirmText: string;
  cancelText: string;
  confirmLoading?: boolean;
  confirmDisabled?: boolean;
  // Why confirming is not possible yet, shown on hover of the disabled button.
  confirmTooltip?: string;
  type?: "warning" | "danger" | "info";
  showIcon?: boolean;
  children?: ReactNode;
};

const ConfirmationModal: FC<ConfirmationModalProps> = ({
  open,
  onCancel,
  onConfirm,
  onClose,
  title,
  description,
  confirmText,
  cancelText,
  confirmLoading = false,
  confirmDisabled = false,
  confirmTooltip,
  type = "warning",
  showIcon = true,
  children,
}) => {
  const hasBody = !!description || !!children;

  const contentClassName = useMemo(() => {
    if (hasBody) {
      return "confirmation-modal__content";
    }

    return "confirmation-modal__content confirmation-modal__content--title-only";
  }, [hasBody]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      // Dismissing mid-action would hide it without stopping it.
      maskClosable={!confirmLoading}
      closable={!confirmLoading}
      keyboard={!confirmLoading}
      footer={[
        <Button
          key="cancel"
          danger
          onClick={onCancel}
          disabled={confirmLoading}
        >
          {cancelText}
        </Button>,
        <Tooltip key="confirm" title={confirmDisabled ? confirmTooltip : ""}>
          <Button
            type="primary"
            onClick={onConfirm}
            loading={confirmLoading}
            disabled={confirmDisabled}
          >
            {confirmText}
          </Button>
        </Tooltip>,
      ]}
      className="confirmation-modal"
    >
      <div className={contentClassName}>
        {showIcon && <StatusIcon type={type} size={32} marginRight="0px" />}
        <div className="confirmation-modal__text">
          <h3>{title}</h3>
          {description && <p>{description}</p>}
          {children}
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmationModal;
